import { useCallback, useRef, useState } from "react";

/**
 * True when the case's `team` holds anyone other than the owner — i.e. the
 * case is currently shared. The owner may or may not be in `team` itself.
 */
export function isCaseShared(caseData, ownerUid) {
  const team = caseData?.team;
  if (!Array.isArray(team)) return false;
  return team.some((uid) => uid && uid !== ownerUid);
}

/**
 * Asked before disposing shared case(s). Usage:
 *
 *   const [askKeepSharing, sharingModal] = useDisposeSharingPrompt();
 *   const keep = await askKeepSharing(sharedCount); // true | false | null
 *   ...
 *   return <>{...}{sharingModal}</>;
 *
 * Resolves `true` to keep sharing (the disposed case stays visible to the
 * people it was shared with), `false` to revoke their access, or `null` if
 * the user backed out of disposing.
 */
export function useDisposeSharingPrompt() {
  const [sharedCount, setSharedCount] = useState(0);
  const resolverRef = useRef(null);

  const ask = useCallback(
    (count = 1) =>
      new Promise((resolve) => {
        resolverRef.current = resolve;
        setSharedCount(count);
      }),
    []
  );

  const finish = (value) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setSharedCount(0);
  };

  const modal = sharedCount > 0 && (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[60]">
      <div className="bg-[#1a1a2e] p-6 rounded-lg border border-gray-700 max-w-md w-full mx-4">
        <h3 className="text-white text-lg font-semibold mb-4">Shared case</h3>
        <p className="text-gray-300 mb-3">
          {sharedCount === 1
            ? "This case is shared with other people."
            : `${sharedCount} of these cases are shared with other people.`}{" "}
          Do you want to keep sharing or revoke sharing?
        </p>
        <ul className="text-gray-400 text-sm mb-6 space-y-1">
          <li>
            <span className="text-[#C99F4A] font-medium">Keep sharing:</span>{" "}
            they will still see the case, now as disposed.
          </li>
          <li>
            <span className="text-red-500 font-medium">Revoke sharing:</span>{" "}
            their access to the case will be removed.
          </li>
        </ul>
        <div className="flex justify-end gap-3 flex-wrap">
          <button
            onClick={() => finish(null)}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={() => finish(false)}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Revoke sharing
          </button>
          <button
            onClick={() => finish(true)}
            className="px-4 py-2 bg-[#C99F4A] text-[#0f0f23] rounded font-medium hover:bg-[#D19B15]"
          >
            Keep sharing
          </button>
        </div>
      </div>
    </div>
  );

  return [ask, modal];
}
